import {
  CheckboxVisibility,
  DetailsListLayoutMode,
  IDetailsGroupRenderProps,
  SelectionMode,
  SelectionZone,
} from "@fluentui/react";
import React, { FC, useEffect } from "react";
import { useSelector } from "react-redux";
import { AnalyticsName } from "../../../constants";
import {
  selectActiveWellbore,
  selectCuttings,
  selectSelectedCutting,
  StoreState,
  useAppDispatch,
} from "../../../store";
import { LasFileJson, TopGroup } from "../../../types";
import { DetailsList } from "../../styledFluentComponents";
import { fetchAnalyticsData } from "../analyticsSlice";
import { columnsConfig, PDFtoolTipTexts, xrdConfig } from "../config";
import {
  useItemsAndGroups,
  useSortMethod,
  useStoredSelection,
} from "../utils/hooks";
import { selections } from "../utils/selections";
import {
  getOnRenderRow,
  getStructuredDataFromLasFile,
  renderGroupHeader,
} from "../utils/tab-helpers";
import ItemsAndGroupsFilteredMessage from "./ItemsAndGroupsFilteredMessage";
import { SelectedItemsClearer } from "./SelectedItemsClearer";
import { TabHeader } from "./TabHeader";

const listGroupProps: IDetailsGroupRenderProps = {
  onRenderHeader: (props) => {
    return renderGroupHeader(props, xrdConfig, undefined);
  },
};
const onRenderRow = getOnRenderRow(AnalyticsName.XRD, xrdConfig);

const XRDTabImpl: FC = () => {
  const dispatch = useAppDispatch();
  const selectedCutting = useSelector(selectSelectedCutting);
  const activeWellbore = useSelector(selectActiveWellbore);
  const cuttings = useSelector(selectCuttings);

  const selectedElements = useStoredSelection(AnalyticsName.XRD);
  const data = useSelector((state: StoreState) => state.analytics.srcData.xrd);
  const [sortMethod, onSortMethodChange] = useSortMethod();

  useEffect(() => {
    if (cuttings) {
      const dataTransformer = (src: LasFileJson, wellTops: TopGroup[]) => {
        return getStructuredDataFromLasFile(src, wellTops, xrdConfig, cuttings);
      };

      dispatch(
        fetchAnalyticsData(
          AnalyticsName.XRD,
          activeWellbore?.analyticsData?.xrd?.dataReference,
          dataTransformer
        )
      );
    }
  }, [dispatch, activeWellbore?.analyticsData?.xrd?.dataReference, cuttings]);

  const {
    items,
    groups,
    filterActive,
    setFilterActive,
    setFilterText,
    groupsCollapsed,
    toggleCollapseAllGroups,
    clearSelection,
    allItemsAndGroupsFiltered,
  } = useItemsAndGroups(
    data,
    selectedCutting,
    selections[AnalyticsName.XRD],
    sortMethod
  );

  return (
    <>
      <TabHeader
        filterActive={filterActive}
        setFilterActive={setFilterActive}
        setFilterText={setFilterText}
        groupsCollapsed={groupsCollapsed}
        toggleCollapseAllGroups={toggleCollapseAllGroups}
        informationTooltipText={PDFtoolTipTexts.xrdData}
        informationPDFUrl={
          activeWellbore?.analyticsData?.xrd?.methodologyReference
        }
        sortMethod={sortMethod}
        onSortMethodChange={onSortMethodChange}
        tab={AnalyticsName.XRD}
        disableExpand={true}
      />

      <ItemsAndGroupsFilteredMessage
        allItemsAndGroupsFiltered={allItemsAndGroupsFiltered}
      />

      <SelectionZone
        className="analytics-pane__selection-zone"
        selection={selections[AnalyticsName.XRD]}
        selectionMode={SelectionMode.multiple}
        selectionPreservedOnEmptyClick={true}
      >
        <DetailsList
          columns={columnsConfig}
          items={items}
          groups={groups}
          onRenderRow={onRenderRow}
          groupProps={listGroupProps}
          compact={true}
          isHeaderVisible={false}
          checkboxVisibility={CheckboxVisibility.always}
          setKey="set"
          getKey={(item) => item.key}
          layoutMode={DetailsListLayoutMode.justified}
          selection={selections[AnalyticsName.XRD]}
          selectionMode={SelectionMode.multiple}
          selectionPreservedOnEmptyClick={true}
        />
      </SelectionZone>
      <SelectedItemsClearer
        numberOfSelectedItems={selectedElements.length}
        onClearSelection={clearSelection}
      />
    </>
  );
};

export const XRDTab = React.memo(XRDTabImpl);
